import { CheckCircle, Clock, XCircle, Info, MinusCircle } from 'lucide-react';

/**
 * Unified Status Configuration
 *
 * Single source of truth for status-to-color tier mapping and display labels
 * across the entire HR Portal. Every status badge in the app derives from this file.
 *
 * Color tiers (using -50 bg / -700 text / ring pattern):
 *   success  = green   → completed, positive outcome, healthy state
 *   warning  = orange  → awaiting action, needs attention, holding state
 *   error    = red     → negative outcome, problem, requires intervention
 *   info     = blue    → in progress, actively being worked on
 *   neutral  = gray    → inactive, terminal neutral state, no action needed
 */

export const TIERS = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info',
  NEUTRAL: 'neutral',
};

export const STATUS_CONFIG = {
  // ── Success (Green) ──────────────────────────────────────────
  approved:             { tier: TIERS.SUCCESS, label: 'Approved' },
  auto_approved:        { tier: TIERS.SUCCESS, label: 'Auto Approved' },
  verified:             { tier: TIERS.SUCCESS, label: 'Verified' },
  completed:            { tier: TIERS.SUCCESS, label: 'Completed' },
  fulfilled:            { tier: TIERS.SUCCESS, label: 'Fulfilled' },
  active:               { tier: TIERS.SUCCESS, label: 'Active' },
  available:            { tier: TIERS.SUCCESS, label: 'Available' },
  confirmed:            { tier: TIERS.SUCCESS, label: 'Confirmed' },
  finalized:            { tier: TIERS.SUCCESS, label: 'Finalized' },
  payroll_locked:       { tier: TIERS.SUCCESS, label: 'Locked' },
  exempt:               { tier: TIERS.SUCCESS, label: 'Exempt' },
  on_track:             { tier: TIERS.SUCCESS, label: 'On Track' },
  sent:                 { tier: TIERS.SUCCESS, label: 'Sent' },
  not_at_risk:          { tier: TIERS.SUCCESS, label: 'Not at risk' },
  safe:                 { tier: TIERS.SUCCESS, label: 'Within cap' },
  pending_dependent:    { tier: TIERS.WARNING, label: 'Safe if approved' },
  scheduled:            { tier: TIERS.INFO, label: 'Scheduled' },
  not_due:              { tier: TIERS.NEUTRAL, label: 'Not due' },
  suppressed:           { tier: TIERS.NEUTRAL, label: 'Suppressed' },
  notified:             { tier: TIERS.SUCCESS, label: 'Notified' },
  first_breach:         { tier: TIERS.ERROR, label: 'First breach' },
  ceiling_reached:      { tier: TIERS.ERROR, label: 'Ceiling' },
  false_refire:         { tier: TIERS.WARNING, label: 'False re-fire' },
  course_approved:      { tier: TIERS.SUCCESS, label: 'Approved' },
  compliant:            { tier: TIERS.SUCCESS, label: 'OK' },
  acknowledged:         { tier: TIERS.SUCCESS, label: 'Acknowledged' },
  returned:             { tier: TIERS.SUCCESS, label: 'Returned' },
  published:            { tier: TIERS.SUCCESS, label: 'Published' },
  eligible:             { tier: TIERS.SUCCESS, label: 'Eligible' },
  passed:               { tier: TIERS.SUCCESS, label: 'Passed' },
  done:                 { tier: TIERS.SUCCESS, label: 'Done' },
  heat_low:             { tier: TIERS.SUCCESS, label: 'Low heat' },
  heat_clear:           { tier: TIERS.SUCCESS, label: 'No heat alerts' },

  // ── Warning (Orange) ────────────────────────────────────────
  training_gap:         { tier: TIERS.WARNING, label: 'Training Gap' },
  expiring_soon:        { tier: TIERS.WARNING, label: 'Expiring Soon' },
  pending:              { tier: TIERS.WARNING, label: 'Pending' },
  pending_ocr:          { tier: TIERS.INFO, label: 'Reading letter' },
  verification_required: { tier: TIERS.WARNING, label: 'Review required' },
  manual_recovery:      { tier: TIERS.WARNING, label: 'Manual recovery' },
  unverified:           { tier: TIERS.NEUTRAL, label: 'Not Verified' },
  submitted:            { tier: TIERS.WARNING, label: 'Submitted' },
  hospitalisation_pending_documents: { tier: TIERS.WARNING, label: 'Certificate pending' },
  pending_hod_approval: { tier: TIERS.WARNING, label: 'Pending HOD' },
  hod_approved:         { tier: TIERS.WARNING, label: 'Pending HR' },
  pending_acceptance:   { tier: TIERS.WARNING, label: 'Pending Acceptance' },
  pending_receipt:      { tier: TIERS.WARNING, label: 'Pending Receipt' },
  pending_completion:   { tier: TIERS.WARNING, label: 'Pending Completion' },
  pending_registration: { tier: TIERS.WARNING, label: 'Pending Registration' },
  pending_confirmation: { tier: TIERS.WARNING, label: 'Pending Confirmation' },
  in_repair:            { tier: TIERS.WARNING, label: 'In Repair' },
  at_workshop:          { tier: TIERS.WARNING, label: 'At Workshop' },
  reserved:             { tier: TIERS.WARNING, label: 'Reserved' },
  on_hold:              { tier: TIERS.WARNING, label: 'On Hold' },
  paused:               { tier: TIERS.WARNING, label: 'Paused' },
  closed:               { tier: TIERS.WARNING, label: 'Closed' },
  planning:             { tier: TIERS.WARNING, label: 'Planning' },
  watch:                { tier: TIERS.WARNING, label: 'Watch' },
  replan:               { tier: TIERS.WARNING, label: 'Re-plan now' },
  projected_exceed:     { tier: TIERS.WARNING, label: 'Projected Exceed' },
  at_risk:              { tier: TIERS.WARNING, label: 'At Risk' },
  warning:              { tier: TIERS.WARNING, label: 'Warning' },
  partial:              { tier: TIERS.WARNING, label: 'Partial' },
  due:                  { tier: TIERS.WARNING, label: 'Due' },
  review_required:      { tier: TIERS.WARNING, label: 'Needs Review' },
  heat_high:            { tier: TIERS.WARNING, label: 'High heat' },
  heat_moderate:        { tier: TIERS.WARNING, label: 'Moderate heat' },

  // ── Traffic offence notices ─────────────────────────────────
  // `closed` is already claimed globally as a warning tier, so a resolved
  // offence uses its own key to stay green.
  pending_driver_confirmation: { tier: TIERS.WARNING, label: 'Awaiting Driver' },
  pending_declaration:  { tier: TIERS.WARNING, label: 'Declare to Authority' },
  pending_payment:      { tier: TIERS.WARNING, label: 'Awaiting Payment' },
  disputed:             { tier: TIERS.ERROR,   label: 'Disputed' },
  offence_closed:       { tier: TIERS.SUCCESS, label: 'Closed' },

  // ── Vehicle accidents ───────────────────────────────────────
  // `closed` is globally warning-tier; a resolved accident uses its own key
  // (mirrors offence_closed). Fault model values `shared` / `undetermined`
  // map to shared_fault / fault_undetermined via badgeStatusFor helpers.
  reported:             { tier: TIERS.ERROR,   label: 'Reported' },
  pending_documents:    { tier: TIERS.WARNING, label: 'Pending Documents' },
  in_claim:             { tier: TIERS.INFO,    label: 'In Claim' },
  accident_closed:      { tier: TIERS.SUCCESS, label: 'Closed' },

  // ── Attendance events ───────────────────────────────────────
  // Global `closed` is warning-tier. An attendance event after
  // "Close & email PDF" is done for host scan purposes (Past scope),
  // so list/detail map via badgeStatusForEvent → event_closed (neutral).
  event_closed:         { tier: TIERS.NEUTRAL, label: 'Closed' },

  at_fault:             { tier: TIERS.ERROR,   label: 'At Fault' },
  not_at_fault:         { tier: TIERS.SUCCESS, label: 'Not At Fault' },
  shared_fault:         { tier: TIERS.WARNING, label: 'Shared Fault' },
  fault_undetermined:   { tier: TIERS.NEUTRAL, label: 'Undetermined' },

  claim_not_started:        { tier: TIERS.NEUTRAL, label: 'Claim Not Started' },
  claim_submitted:          { tier: TIERS.WARNING, label: 'Claim Submitted' },
  claim_under_assessment:   { tier: TIERS.INFO,    label: 'Under Assessment' },
  claim_approved:           { tier: TIERS.SUCCESS, label: 'Claim Approved' },
  claim_rejected:           { tier: TIERS.ERROR,   label: 'Claim Rejected' },
  claim_settled:            { tier: TIERS.SUCCESS, label: 'Claim Settled' },
  claim_private_settled:    { tier: TIERS.SUCCESS, label: 'Privately Settled' },
  claim_withdrawn:          { tier: TIERS.NEUTRAL, label: 'Claim Withdrawn' },

  reporting_only:       { tier: TIERS.INFO,    label: 'Reporting Only' },
  third_party_claim:    { tier: TIERS.WARNING, label: 'Third Party Claim' },
  own_damage_claim:     { tier: TIERS.WARNING, label: 'Own Damage Claim' },
  no_reporting:         { tier: TIERS.ERROR,   label: 'No Reporting' },

  // ── Error (Red) ─────────────────────────────────────────────
  expired:              { tier: TIERS.ERROR, label: 'Expired' },
  rejected:             { tier: TIERS.ERROR, label: 'Rejected' },
  hod_rejected:         { tier: TIERS.ERROR, label: 'HOD Rejected' },
  hr_rejected:          { tier: TIERS.ERROR, label: 'HR Rejected' },
  failed:               { tier: TIERS.ERROR, label: 'Failed' },
  breached_during_course: { tier: TIERS.ERROR, label: 'Breached (Course)' },
  breached_during_bond: { tier: TIERS.ERROR, label: 'Breached (Bond)' },
  lost:                 { tier: TIERS.ERROR, label: 'Lost' },
  exceeded:             { tier: TIERS.ERROR, label: 'Exceeded' },
  imminent:             { tier: TIERS.ERROR, label: 'Imminent' },
  breach:               { tier: TIERS.ERROR, label: 'Breach' },
  enqueue_failed:       { tier: TIERS.ERROR, label: 'Failed' },
  critical:             { tier: TIERS.ERROR, label: 'Critical' },
  non_compliant:        { tier: TIERS.ERROR, label: 'Violated' },
  violated:             { tier: TIERS.ERROR, label: 'Violated' },
  taxable:              { tier: TIERS.ERROR, label: 'Taxable' },
  not_eligible:         { tier: TIERS.ERROR, label: 'Not Eligible' },
  pending_disposal:     { tier: TIERS.ERROR, label: 'Pending Disposal' },
  written_off:          { tier: TIERS.ERROR, label: 'Written Off' },
  overdue:              { tier: TIERS.ERROR, label: 'Overdue' },
  overdue_not_sent:     { tier: TIERS.ERROR, label: 'Overdue — not sent' },
  flagged:              { tier: TIERS.ERROR, label: 'Flagged' },
  reconciliation_required: { tier: TIERS.ERROR, label: 'Reconciliation required' },
  heat_extreme:         { tier: TIERS.ERROR, label: 'Extreme heat' },

  // ── Overseas travel notice (active / no-approval awareness) ─
  travel_notice:        { tier: TIERS.INFO, label: 'Travel notice' },

  // ── Info (Blue) ─────────────────────────────────────────────
  pre_onboarding:       { tier: TIERS.INFO, label: 'Pre-Onboarding' },
  offered:              { tier: TIERS.WARNING, label: 'Offered' },
  accepted:             { tier: TIERS.INFO, label: 'Offer Accepted' },
  blocked:              { tier: TIERS.WARNING, label: 'Blocked' },
  ready_to_hire:        { tier: TIERS.SUCCESS, label: 'Ready to Hire' },
  hired:                { tier: TIERS.SUCCESS, label: 'Hired' },
  rescinded:            { tier: TIERS.NEUTRAL, label: 'Rescinded' },
  in_progress:          { tier: TIERS.INFO, label: 'In Progress' },
  assigned:             { tier: TIERS.INFO, label: 'Assigned' },
  deployed:             { tier: TIERS.INFO, label: 'Deployed' },
  not_started:          { tier: TIERS.INFO, label: 'Not Started' },
  processing:           { tier: TIERS.INFO, label: 'Processing' },
  live:                 { tier: TIERS.INFO, label: 'Live' },
  processing_ocr:       { tier: TIERS.INFO, label: 'Processing' },
  ocr_completed:        { tier: TIERS.WARNING, label: 'Pending Review' },
  pending_review:       { tier: TIERS.INFO, label: 'Pending Review' },
  pending_verification: { tier: TIERS.WARNING, label: 'Pending Review' },
  confirm_and_send:     { tier: TIERS.WARNING, label: 'Confirm & send' },
  awaiting_employee:    { tier: TIERS.INFO, label: 'Awaiting employee' },
  new_upload:           { tier: TIERS.INFO, label: 'New Upload' },
  required:             { tier: TIERS.WARNING, label: 'Required' },
  pending_hr:           { tier: TIERS.WARNING, label: 'Pending HR' },
  under_review:         { tier: TIERS.WARNING, label: 'Under Review' },
  awaiting_approval:    { tier: TIERS.WARNING, label: 'Awaiting Approval' },
  invalid:              { tier: TIERS.ERROR, label: 'Invalid' },

  // ── NS leave ───────────────────────────────────────────────
  pending_ns_verification: { tier: TIERS.WARNING, label: 'Pending NS Verification' },

  // ── Nightwork-specific ──────────────────────────────────────
  pending_deputy_hod:   { tier: TIERS.WARNING, label: 'Pending Dy HOD' },
  pending_hod:          { tier: TIERS.WARNING, label: 'Pending HOD' },
  frozen:               { tier: TIERS.INFO, label: 'Amendment Pending' },
  superseded:           { tier: TIERS.NEUTRAL, label: 'Superseded' },

  // ── Course Requests ─────────────────────────────────────────
  // Portal `approved` = awaiting booking (needs HR action) — not global green "Approved".
  awaiting_booking:     { tier: TIERS.WARNING, label: 'Awaiting booking' },
  booked:               { tier: TIERS.INFO, label: 'Booked' },
  cancelled_by_hr:      { tier: TIERS.NEUTRAL, label: 'Cancelled by HR' },
  pass:                 { tier: TIERS.SUCCESS, label: 'Pass' },
  fail:                 { tier: TIERS.ERROR, label: 'Fail' },

  // ── Flight ticket sub-statuses (Home Leave / Urgent Leave) ──
  ticket_required:      { tier: TIERS.WARNING, label: 'Upload Flight Ticket' },
  ticket_under_review:  { tier: TIERS.INFO,    label: 'Ticket Under Review' },
  ticket_rejected:      { tier: TIERS.ERROR,   label: 'Ticket Rejected' },

  // ── Attendance Exception statuses ───────────────────────────
  in_review:            { tier: TIERS.INFO,    label: 'In Review' },
  resolved:             { tier: TIERS.SUCCESS, label: 'Resolved' },
  dismissed:            { tier: TIERS.NEUTRAL, label: 'Dismissed' },

  // ── PCP matching statuses ─────────────────────────────────
  matched:              { tier: TIERS.SUCCESS, label: 'Matched' },
  unmatched:            { tier: TIERS.WARNING, label: 'Unmatched' },
  replaced:             { tier: TIERS.NEUTRAL, label: 'Replaced' },
  edited:               { tier: TIERS.INFO,    label: 'Edited' },

  // ── Probation ─────────────────────────────────────────────
  probation_extended:   { tier: TIERS.INFO,    label: 'Extended' },

  // ── Asset return reasons (employee-initiated return requests) ──
  no_longer_needed:     { tier: TIERS.NEUTRAL, label: 'No Longer Needed' },
  faulty:               { tier: TIERS.ERROR,   label: 'Faulty/Not Working' },
  damaged:              { tier: TIERS.WARNING, label: 'Damaged' },
  upgrade_requested:    { tier: TIERS.INFO,    label: 'Upgrade Requested' },
  other:                { tier: TIERS.NEUTRAL, label: 'Other' },

  // ── Neutral (Gray) ──────────────────────────────────────────
  monitor:              { tier: TIERS.NEUTRAL, label: 'Monitor' },
  skipped_no_email:     { tier: TIERS.NEUTRAL, label: 'No Email' },
  skipped_empty:        { tier: TIERS.NEUTRAL, label: 'Quiet run' },
  disabled:             { tier: TIERS.NEUTRAL, label: 'Disabled' },
  // Service API Keys — permanently revoked (distinct from reversible Disabled).
  revoked:              { tier: TIERS.ERROR, label: 'Revoked' },
  cancelled:            { tier: TIERS.NEUTRAL, label: 'Cancelled' },
  withdrawn:            { tier: TIERS.NEUTRAL, label: 'Withdrawn' },
  draft:                { tier: TIERS.NEUTRAL, label: 'Draft' },
  on_file:              { tier: TIERS.NEUTRAL, label: 'On file' },
  disposed:             { tier: TIERS.NEUTRAL, label: 'Disposed' },
  deactivated:          { tier: TIERS.NEUTRAL, label: 'Deactivated' },
  terminated:           { tier: TIERS.NEUTRAL, label: 'Terminated' },
  returned_to_lessor:   { tier: TIERS.NEUTRAL, label: 'Returned to Lessor' },
  waived:               { tier: TIERS.NEUTRAL, label: 'Waived' },
  scrapped:             { tier: TIERS.NEUTRAL, label: 'Scrapped' },
  sold:                 { tier: TIERS.NEUTRAL, label: 'Sold' },
  archived:             { tier: TIERS.NEUTRAL, label: 'Archived' },
  inactive:             { tier: TIERS.NEUTRAL, label: 'Inactive' },
  locked:               { tier: TIERS.NEUTRAL, label: 'Locked' },
  payroll_unlocked:     { tier: TIERS.NEUTRAL, label: 'Unlocked' },
  upcoming:             { tier: TIERS.NEUTRAL, label: 'Upcoming' },
  heat_unknown:         { tier: TIERS.NEUTRAL, label: 'Unknown' },

  awaiting_teams_recommendation: { tier: TIERS.WARNING, label: 'Awaiting recommendation' },
  awaiting_manager_recommendation: { tier: TIERS.WARNING, label: 'Awaiting recommendation' },
  awaiting_department_approval: { tier: TIERS.WARNING, label: 'Awaiting department approval' },
  configuration_exception: { tier: TIERS.ERROR, label: 'HR configuration exception' },
  approved_retain:      { tier: TIERS.INFO, label: 'Approved — retain' },
  approved_end:         { tier: TIERS.INFO, label: 'Approved — end at expiry' },
  pending_hr_action:    { tier: TIERS.WARNING, label: 'Pending HR action' },
  in_preparation:       { tier: TIERS.INFO, label: 'In preparation' },
  submitted_to_mom:     { tier: TIERS.INFO, label: 'Submitted to MOM' },
  renewed:              { tier: TIERS.SUCCESS, label: 'Renewed' },
  failed:               { tier: TIERS.ERROR, label: 'Failed' },
  awaiting_worker_acknowledgement: { tier: TIERS.WARNING, label: 'Awaiting worker acknowledgement' },
  awaiting_department_acknowledgement: { tier: TIERS.WARNING, label: 'Awaiting department acknowledgement' },
  returned_for_correction: { tier: TIERS.WARNING, label: 'Returned for correction' },
  disputed:             { tier: TIERS.ERROR, label: 'Disputed' },
  pending_hr_review:    { tier: TIERS.WARNING, label: 'Pending HR review' },
  legacy:               { tier: TIERS.NEUTRAL, label: 'Legacy history' },

  // ── Recruitment / ATS ───────────────────────────────────────
  new:                  { tier: TIERS.WARNING, label: 'New' },
  screening:            { tier: TIERS.INFO,    label: 'HR Review' },
  kiv:                  { tier: TIERS.WARNING, label: 'Keep in View' },
  interview_scheduled:  { tier: TIERS.INFO,    label: 'Interview Scheduled' },
  interview_completed:  { tier: TIERS.INFO,    label: 'Interview Completed' },
  offer_pending:        { tier: TIERS.WARNING, label: 'Offer Pending' },
  offer_sent:           { tier: TIERS.WARNING, label: 'Offer Sent' },
  offer_accepted:       { tier: TIERS.SUCCESS, label: 'Offer Accepted' },
  offer_rejected:       { tier: TIERS.ERROR,   label: 'Offer Rejected' },
};

export const TIER_CLASSES = {
  [TIERS.SUCCESS]: 'bg-green-50 text-green-700 ring-green-600/20',
  [TIERS.WARNING]: 'bg-orange-50 text-orange-700 ring-orange-600/20',
  [TIERS.ERROR]:   'bg-red-50 text-red-700 ring-red-600/20',
  [TIERS.INFO]:    'bg-blue-50 text-blue-700 ring-blue-600/20',
  [TIERS.NEUTRAL]: 'bg-gray-100 text-gray-700 ring-gray-600/20',
};

export const TIER_ICONS = {
  [TIERS.SUCCESS]: CheckCircle,
  [TIERS.WARNING]: Clock,
  [TIERS.ERROR]:   XCircle,
  [TIERS.INFO]:    Info,
  [TIERS.NEUTRAL]: MinusCircle,
};

// ── Helper Functions ──────────────────────────────────────────

function formatStatusString(status) {
  if (!status) return '';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getTierForStatus(status) {
  return STATUS_CONFIG[status]?.tier || TIERS.NEUTRAL;
}

export function getLabelForStatus(status) {
  return STATUS_CONFIG[status]?.label || formatStatusString(status);
}

export function getTierClasses(status) {
  return TIER_CLASSES[getTierForStatus(status)];
}

export function getIconForStatus(status) {
  return TIER_ICONS[getTierForStatus(status)];
}
